import type {
  AppliedChatbotConfig,
  AppliedEventKey,
  AppliedMetadata,
  ChatbotLogger,
  ChatEventCallback,
  ChatMessageCallback,
} from './types';
import { createChatbotVendorAdapter } from './vendors';
import type { ChatbotVendor, ChatbotVendorAdapter } from './vendors';

export interface ChatbotInstanceOptions {
  config: AppliedChatbotConfig;
  logger?: ChatbotLogger | Console;
  /** Defaults to Applied Labs. */
  vendor?: ChatbotVendor;
}

/** Owns config/logger and the mount lifecycle, delegating vendor-specific calls to the adapter. */
export class Chatbot {
  private config: AppliedChatbotConfig;
  private logger: ChatbotLogger | Console;
  private adapter: ChatbotVendorAdapter;

  constructor({ config, logger, vendor }: ChatbotInstanceOptions) {
    this.config = config;
    this.logger = logger ?? console;
    this.adapter = createChatbotVendorAdapter(vendor);
  }

  injectScript() {
    this.adapter.injectScript(this.logger);
  }

  injectElement() {
    this.adapter.injectElement(this.config, this.logger);
  }

  setMetadata(metadata: AppliedMetadata) {
    this.adapter.setMetadata(metadata, this.logger);
  }

  updateMetadata(metadata: Partial<AppliedMetadata>) {
    this.setMetadata({ ...this.config.metadata, ...metadata });
  }

  show() {
    this.adapter.show(this.logger);
  }

  hide() {
    this.adapter.hide(this.logger);
  }

  setOpen(open: boolean) {
    this.adapter.setOpen(open, this.logger);
  }

  onMessage(callback: ChatMessageCallback) {
    this.adapter.setOnMessage(callback, this.logger);
  }

  subscribeToEvent<T>(event: AppliedEventKey, callback: ChatEventCallback<T>): number {
    return this.adapter.subscribeToEvent(event, callback, this.logger);
  }

  unsubscribeFromEvent(subscriptionId: number) {
    this.adapter.unsubscribeFromEvent(subscriptionId, this.logger);
  }

  fireEvent(event: string, payload?: unknown) {
    this.adapter.fireEvent(event, payload, this.logger);
  }

  trackEvent(event: string, value?: unknown, meta?: Record<string, unknown>) {
    this.adapter.trackEvent(event, value, meta, this.logger);
  }

  // Custom methods
  mount(config?: Partial<AppliedChatbotConfig>) {
    this.config = { ...this.config, ...config };
    this.injectScript();
    this.injectElement();
  }

  unmount() {
    this.adapter.removeElement(this.logger);
    this.adapter.removeScript(this.logger);
  }

  isMounted() {
    return this.adapter.isMounted();
  }
}
